import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Mic, Paperclip, Plus, SendHorizontal } from 'lucide-react';
import { cn } from '@clarix/ui';

export interface HomeComposerProps {
  /** Fires with the trimmed text. The field clears itself afterwards. */
  onSend: (text: string) => void;
  placeholder?: string;
  /** Small hint line between the icons and the send button. */
  hint?: ReactNode;
  /** Each optional handler renders its icon; omit one and it disappears. */
  onAttach?: () => void;
  onAdd?: () => void;
  onDictate?: () => void;
  /** Blocks sending while a reply is still in flight. */
  isBusy?: boolean;
  autoFocus?: boolean;
  className?: string;
}

const ICON_BUTTON_CLASS =
  'grid h-8 w-8 shrink-0 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--primary))]/30 disabled:pointer-events-none disabled:opacity-50';

/**
 * The composer under the hero. Typing is optional — tapping a starter is
 * usually faster — so the field stays quiet until it has something in it,
 * at which point the send button lights up.
 */
export function HomeComposer({
  onSend,
  placeholder,
  hint,
  onAttach,
  onAdd,
  onDictate,
  isBusy = false,
  autoFocus,
  className,
}: HomeComposerProps) {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow up to the max height, then scroll. Height is a computed value
  // with no class equivalent, so it is written to the node imperatively
  // rather than as an inline `style` prop (UI rule 1).
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  const canSend = text.trim().length > 0 && !isBusy;

  const submit = useCallback(() => {
    const value = text.trim();
    if (!value || isBusy) return;
    onSend(value);
    setText('');
  }, [text, isBusy, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter sends; Shift+Enter keeps the newline.
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  return (
    <div className={cn('shrink-0 px-6 pb-5 pt-1.5', className)}>
      <div className="mx-auto max-w-[780px]">
        <div className="border-border bg-surface focus-within:border-primary/25 focus-within:ring-primary/15 rounded-[20px] border-[1.5px] shadow-sm transition-all focus-within:ring-[3px]">
          <label className="sr-only" htmlFor="app-home-composer">
            Message
          </label>
          <textarea
            id="app-home-composer"
            ref={textareaRef}
            rows={2}
            value={text}
            autoFocus={autoFocus}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="text-foreground placeholder:text-muted-foreground block max-h-[150px] min-h-[52px] w-full resize-none border-none bg-transparent px-4 pb-1 pt-4 text-[15px] leading-relaxed outline-none"
          />

          <div className="flex items-center gap-1.5 px-3 pb-2.5 pt-1">
            {onAttach ? (
              <button
                type="button"
                onClick={onAttach}
                title="Attach"
                aria-label="Attach"
                className={ICON_BUTTON_CLASS}
              >
                <Paperclip className="h-4 w-4" strokeWidth={1.5} />
              </button>
            ) : null}
            {onAdd ? (
              <button
                type="button"
                onClick={onAdd}
                title="Add something"
                aria-label="Add something"
                className={ICON_BUTTON_CLASS}
              >
                <Plus className="h-4 w-4" strokeWidth={1.5} />
              </button>
            ) : null}

            {hint ? (
              <span className="text-muted-foreground truncate text-[11px]">{hint}</span>
            ) : null}

            <span className="flex-1" />

            {onDictate ? (
              <button
                type="button"
                onClick={onDictate}
                title="Speak instead"
                aria-label="Speak instead"
                className={ICON_BUTTON_CLASS}
              >
                <Mic className="h-4 w-4" strokeWidth={1.5} />
              </button>
            ) : null}

            <button
              type="button"
              onClick={submit}
              disabled={!canSend}
              aria-label="Send"
              title="Send"
              className={cn(
                'grid h-9 w-9 shrink-0 place-items-center rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--primary))]/30',
                canSend
                  ? 'bg-primary text-primary-foreground shadow-primary/25 shadow-md hover:-translate-y-px hover:shadow-lg'
                  : 'bg-border text-muted-foreground cursor-not-allowed',
              )}
            >
              <SendHorizontal className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
